import * as THREE from 'three';
import type { EntityCtx } from '../game/Entity';
import type { Game } from '../game/Game';
import type { EnemyType } from '../game/entities/EnemyPlane';
import { useStore } from '../state/store';

/** Reinforcements are EARNED, never free, and only ever REPLACE losses (oldest first — a downed
 *  fighter, AA gun or tank). You earn one by crossing a score milestone; it then backfills the
 *  next outstanding loss after an arrival cooldown that grows with every reinforcement already
 *  received. Lose units faster than you can earn replacements and the force is whittled down. */
const FIRST_MILESTONE = 2500;    // score for the 1st earned reinforcement
const MILESTONE_GAP_START = 2500; // gap to the next milestone...
const MILESTONE_GAP_GROW = 1000;  // ...which widens by this each time (2500 → 6000 → 10500 → 16000 …)
const ARRIVAL_COOLDOWN_START = 30; // seconds before the 1st earned plane arrives
const ARRIVAL_COOLDOWN_STEP = 5;   // +5s per reinforcement already granted
const ARRIVAL_COOLDOWN_MAX = 60;   // capped at 1 minute — a 2-minute wait while down to one
                                   // pilot stalled late runs into pure attrition

type Phase = 'intro' | 'fighting' | 'cleared';

const INTRO_SECS = 2.0;
const CLEARED_SECS = 1.5;
const SPAWN_INTERVAL = 1.5;

function quotaFor(wave: number): number {
  if (wave <= 1) return 1;
  if (wave === 2) return 2;
  if (wave <= 4) return 3;
  if (wave <= 6) return 5;
  if (wave === 7) return 6;
  if (wave === 8) return 8;
  if (wave === 9) return 10;
  if (wave <= 12) return 12;
  // Keep ramping gently past the old 12-cap so the back half escalates: 13 @13, 16 @19+.
  return Math.min(16, 12 + Math.ceil((wave - 12) / 2));
}

function zeppelinChance(wave: number): number {
  if (wave < 3) return 0;
  if (wave < 6) return 0.2;
  if (wave < 10) return 0.5;
  return 1.0;
}

/**
 * SIEGE PRESSURE — the core "take control" tension. A scaling number of enemy ground
 * tanks is kept bearing down on the villages at all times. Crucially, ground tanks are
 * only counterable by your two allied tanks and your personal intervention (AA guns shoot
 * aircraft, not armour). So once the siege count exceeds your handful of allied tanks, a
 * hands-off, map-only defence steadily loses villages — your forces blunt it but can't stop
 * it. Jumping into a tank (or strafing from the air) at the hot spot is what turns the tide.
 * This does NOT weaken the AI; it just makes the enemy relentless enough to need the ace.
 */
function siegePressure(wave: number): number {
  if (wave < 3) return 0;
  if (wave <= 4) return 1;
  if (wave <= 6) return 2;
  if (wave <= 8) return 3;
  if (wave <= 11) return 4;
  if (wave <= 15) return 5;
  if (wave <= 19) return 6;
  return 7; // end-game: the ground war demands the player's attention too
}
const SIEGE_SPAWN_INTERVAL = 3.0; // seconds between siege-tank reinforcements

/** Pick the type for the i-th enemy spawned during a wave. Pure standard early on,
 *  scouts mix in from W3, bombers from W6 — picks change wave-to-wave so the player
 *  sees variety. */
function pickEnemyType(wave: number, index: number): EnemyType {
  // Bias toward standard but introduce scouts and bombers as the wave climbs.
  const r = (Math.sin(wave * 7.31 + index * 1.97) * 0.5 + 0.5);
  if (wave >= 4 && r < 0.2) return 'bomber';   // air-side village pressure from W4
  if (wave >= 3 && r < 0.45) return 'scout';
  return 'standard';
}

/**
 * Boss rotation: every 3rd wave from W3 onward, cycling through the 8 bosses.
 *   W3:  ace
 *   W6:  carrier
 *   W9:  aether
 *   W12: tesla
 *   W15: juggernaut
 *   W18: colossus
 *   W21: worm
 *   W24: maelstrom
 *   W27+: cycle repeats
 */
export type BossKind = 'ace' | 'carrier' | 'aether' | 'tesla' | 'juggernaut' | 'colossus' | 'worm' | 'maelstrom';

/** Shown as a control hint when the boss spawns — names the machine and HOW to kill it. */
const BOSS_INTRO: Record<BossKind, string> = {
  ace:        'ENEMY ACE ON THE HUNT — OUTFLY HIM',
  carrier:    'SKY CARRIER — DESTROY ITS ZEPPELIN PODS',
  aether:     'AETHER DREADNOUGHT — ITS TOWER VENTS AFTER EACH BROADSIDE',
  tesla:      'TESLA BASTION — KILL THE DRONES, THEN THE COIL',
  juggernaut: 'JUGGERNAUT — HIT THE MAW, HARDEST WHILE IT INHALES',
  colossus:   'CLOCKWORK COLOSSUS — DODGE THE STEAM, STRIKE THE OPEN HEART',
  worm:       'IRON WORM — STRIKE THE COILS THAT BREACH',
  maelstrom:  'MAELSTROM ENGINE — SHOOT OFF THE RODS, THEN THE TURBINE',
};
const BOSS_ROTATION: BossKind[] = ['ace', 'carrier', 'aether', 'tesla', 'juggernaut', 'colossus', 'worm', 'maelstrom'];
/** Clearing this wave = every unique boss defeated → VICTORY. Bosses land on waves 3,6,9… so the
 *  last new one is wave 3 × (#bosses). Hold the line through it and the run is won. */
export const WIN_WAVE = BOSS_ROTATION.length * 3;
export function bossForWave(wave: number): BossKind | null {
  if (wave < 3 || wave % 3 !== 0) return null;
  const idx = (wave / 3 - 1) % BOSS_ROTATION.length;
  return BOSS_ROTATION[idx];
}

export class WaveDirector {
  private phase: Phase = 'intro';
  private phaseT = 0;
  private spawnT = 0;
  private spawnedThisWave = 0;
  private wantZeppelinThisWave = false;
  private zeppelinSpawned = false;
  private bossKind: BossKind | null = null;
  private bossSpawned = false;
  private quota = 1;
  private siegeT = 0;
  // --- Earned reinforcements ---
  private nextMilestone = FIRST_MILESTONE; // score that grants the next reinforcement
  private milestoneGap = MILESTONE_GAP_START;
  private banked = 0;        // earned-but-not-yet-arrived reinforcements
  private granted = 0;       // reinforcements already delivered (drives the growing cooldown)
  private arrivalT = 0;      // seconds counted toward the current reinforcement's arrival

  constructor(private game: Game) {}

  reset() {
    this.phase = 'intro';
    this.phaseT = 0;
    this.spawnT = 0;
    this.spawnedThisWave = 0;
    this.wantZeppelinThisWave = false;
    this.zeppelinSpawned = false;
    this.bossKind = null;
    this.bossSpawned = false;
    this.quota = 1;
    this.siegeT = 0;
    this.nextMilestone = FIRST_MILESTONE;
    this.milestoneGap = MILESTONE_GAP_START;
    this.banked = 0;
    this.granted = 0;
    this.arrivalT = 0;
  }

  /** Earned-reinforcement loop. Crossing a score milestone BANKS a reinforcement (milestones
   *  widen as the score climbs). A banked reinforcement only arrives once there's room under
   *  the squadron cap AND an arrival cooldown elapses — and that cooldown grows with every
   *  reinforcement already delivered, so replacements get slower over a long run. */
  private maintainSquadron(ctx: EntityCtx) {
    const store = useStore.getState();
    // Bank a reinforcement for each milestone the score has crossed.
    while (store.score >= this.nextMilestone) {
      this.banked += 1;
      this.milestoneGap += MILESTONE_GAP_GROW;
      this.nextMilestone += this.milestoneGap;
      store.setControlHint('REINFORCEMENT EARNED');
    }

    const cd = Math.min(ARRIVAL_COOLDOWN_MAX, ARRIVAL_COOLDOWN_START + ARRIVAL_COOLDOWN_STEP * this.granted);
    // A reinforcement only arrives when one is banked AND a friendly was actually lost to replace —
    // reinforcements backfill losses (oldest first), they don't grow the force past its start size.
    const armed = this.banked > 0 && this.game.lostUnits.length > 0;
    if (armed) {
      this.arrivalT += ctx.dt;
      if (this.arrivalT >= cd) {
        this.deliverReinforcement();
        this.banked -= 1;
        this.granted += 1;
        this.arrivalT = 0;
      }
    } else {
      // Nothing earned, or no losses outstanding — disarm the arrival timer so the countdown
      // starts fresh the next time a replacement is actually needed.
      this.arrivalT = 0;
    }

    // Publish status to the HUD so the player can see banked count + arrival ETA.
    store.setReinforce(this.banked, armed ? Math.max(0, Math.ceil(cd - this.arrivalT)) : null);
  }

  /** Replace whatever died first (FIFO). A lost fighter flies in near the player; a lost AA gun or
   *  tank returns to where it fell so the defensive line is rebuilt in place. */
  private deliverReinforcement() {
    const lost = this.game.lostUnits.shift();
    if (!lost) return;
    this.game.audio.playReinforcement();
    if (lost.kind === 'gun') {
      this.game.spawnTownGunner(lost.pos.clone(), lost.label || 'AA GUN', lost.elevate);
      useStore.getState().setControlHint('AA CREW REPLACED');
    } else if (lost.kind === 'tank') {
      this.game.spawnAlliedTank(lost.pos.clone(), lost.label || 'TANK');
      useStore.getState().setControlHint('TANK INBOUND');
    } else {
      const anchor = this.game.controlled?.position ?? new THREE.Vector3(0, 140, 0);
      const a = Math.random() * Math.PI * 2;
      const pos = new THREE.Vector3(
        anchor.x + Math.cos(a) * 420,
        THREE.MathUtils.clamp(anchor.y + (Math.random() - 0.5) * 60, 90, 320),
        anchor.z + Math.sin(a) * 420,
      );
      this.game.spawnAlliedFighter(pos);
      useStore.getState().setControlHint('WINGMAN INBOUND');
    }
  }

  tick(ctx: EntityCtx) {
    // The authored tutorial owns its scripted contacts; suppress normal waves and siege pressure.
    // Ground forces still exist so possession and tactical orders use the real campaign systems.
    if (useStore.getState().tutorial) return;
    this.phaseT += ctx.dt;
    const wave = useStore.getState().wave;
    this.maintainSquadron(ctx);
    if (this.phase === 'intro') {
      if (this.phaseT === ctx.dt) {
        // first tick in intro — (re)seed wave config
        this.quota = quotaFor(wave);
        this.bossKind = bossForWave(wave);
        this.bossSpawned = false;
        // Skip the zeppelin roll on boss waves — the boss is the spectacle.
        this.wantZeppelinThisWave = !this.bossKind && Math.random() < zeppelinChance(wave);
        this.zeppelinSpawned = false;
        this.spawnedThisWave = 0;
        this.spawnT = 0;
      }
      if (this.phaseT >= INTRO_SECS) {
        this.phase = 'fighting';
        this.phaseT = 0;
      }
      return;
    }

    if (this.phase === 'fighting') {
      // Spawn zeppelin once per wave if rolled
      if (this.wantZeppelinThisWave && !this.zeppelinSpawned && this.phaseT > 1.0) {
        this.game.spawnZeppelin();
        this.zeppelinSpawned = true;
      }
      // Single boss per wave from the rotating schedule.
      if (this.bossKind && !this.bossSpawned && this.phaseT > 0.5) {
        this.spawnBoss(this.bossKind);
        this.bossSpawned = true;
      }
      // Siege: keep enemy ground tanks bearing on the villages, topped up to the wave's
      // pressure target. Persistent (doesn't gate wave clear) and only counterable by your
      // allied tanks + you — the steady drain that forces you off the map and into the fight.
      this.siegeT += ctx.dt;
      if (this.siegeT >= SIEGE_SPAWN_INTERVAL) {
        this.siegeT = 0;
        const liveSiege = this.game.findEnemies().filter((e) => e.kind === 'tank' && !e.isBoss && e.alive()).length;
        if (liveSiege < siegePressure(wave)) this.game.spawnTank();
      }

      // Spawn biplanes on cadence until quota reached
      this.spawnT += ctx.dt;
      const active = this.game.findEnemies().filter((e) => e.kind === 'enemy').length;
      const remainingToSpawn = this.quota - this.spawnedThisWave;
      if (remainingToSpawn > 0 && this.spawnT >= SPAWN_INTERVAL && active < this.quota) {
        this.spawnT = 0;
        const type = pickEnemyType(wave, this.spawnedThisWave);
        this.game.spawnEnemyNear(this.game.player.position, type);
        this.spawnedThisWave++;
      }

      // Cleared if WaveDirector has finished its quota AND boss/zeppelin (if rolled)
      // were spawned AND no live enemies remain. Wreckage doesn't count.
      // Grunt tanks are persistent ground threats and don't gate wave progression.
      // Boss tanks (Juggernaut, Colossus, Bastion, Worm, Maelstrom — all carry isBoss)
      // DO gate it. Without this, ground bosses would be skipped on wave clear.
      const liveEnemies = this.game.findEnemies()
        .filter((e) => e.alive() && (e.kind !== 'tank' || e.isBoss)).length;
      const zeppelinReady = !this.wantZeppelinThisWave || this.zeppelinSpawned;
      const bossReady = !this.bossKind || this.bossSpawned;
      if (this.spawnedThisWave >= this.quota && zeppelinReady && bossReady && liveEnemies === 0) {
        this.phase = 'cleared';
        this.phaseT = 0;
        this.game.audio.playWaveCleared();
      }
      return;
    }

    if (this.phase === 'cleared') {
      if (this.phaseT >= CLEARED_SECS) {
        // Cleared the final boss wave → you win.
        if (wave >= WIN_WAVE) {
          useStore.getState().setPhase('victory');
          this.phase = 'intro';
          this.phaseT = 0;
          return;
        }
        const next = wave + 1;
        useStore.getState().setWave(next, ctx.now);
        this.phase = 'intro';
        this.phaseT = 0;
      }
    }
  }

  private spawnBoss(kind: BossKind) {
    switch (kind) {
      case 'ace':        this.game.spawnEnemyNear(this.game.player.position, 'ace'); break;
      case 'carrier':    this.game.spawnSkyCarrier(); break;
      case 'aether':     this.game.spawnAetherDreadnought(); break;
      case 'tesla':      this.game.spawnTeslaBastion(); break;
      case 'juggernaut': this.game.spawnJuggernaut(); break;
      case 'colossus':   this.game.spawnColossus(); break;
      case 'worm':       this.game.spawnIronWorm(); break;
      case 'maelstrom':  this.game.spawnMaelstromEngine(); break;
    }
    // FINALE: the Maelstrom on the winning wave doesn't stand alone — a pair of ace escorts
    // contest the sky over the summit so wave 24 plays like an ending, not another rotation stop.
    if (kind === 'maelstrom' && useStore.getState().wave >= WIN_WAVE) {
      this.game.spawnEnemyNear(this.game.player.position, 'ace');
      this.game.spawnEnemyNear(this.game.player.position, 'ace');
    }
    // Announce the boss AND its kill plan — gated weak points are otherwise trial-and-error.
    useStore.getState().setControlHint(BOSS_INTRO[kind]);
    this.game.audio.playBossSpawn();
  }
}
